// src/components/AdminDashboard/index.tsx
import React, { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "./index.css";
import {
  getAllActiveDrivers
} from "../../services/DriverService.ts";
import { useNavigate } from "react-router-dom";
import VehicleIcon from '../../assets/vehicle.png';
import { 
  Box, 
  Tabs, 
  Tab, 
  Typography, 
  Grid, 
  Paper, 
  CircularProgress,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
} from '@mui/material';
import { getSupportRequests } from '../../services/SupportService.ts';
import { SupportRequest } from '../SupportRequests/types.ts';
import { getStats } from '../../services/CommonService.ts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import PersonIcon from '@mui/icons-material/Person';
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import VerifiedIcon from '@mui/icons-material/Verified';
import PendingIcon from '@mui/icons-material/Pending';
import GroupIcon from '@mui/icons-material/Group';
import { getAllRides, TripStatus } from "../../services/RideService.ts";
import { format, isSameDay, parseISO } from 'date-fns';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';

// Custom marker icon
const icon = new L.Icon({
  iconUrl: VehicleIcon,
  iconSize: [45, 41],
  iconAnchor: [12, 41],
});

interface Vehicle {
  id: number;
  driverId: number;
  vehicle_no: string;
  vehicle_type: string;
}

interface DriverData {
  id: number;
  name: string;
  location: string;
  dob: string;
  email: string | null;
  isVerified: boolean;
  phone: string;
  preferred_language: string;
  vehicles: Vehicle[];
  status: string;
  isActive: boolean;
  current_location: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface SupportStats {
  totalRequests: number;
  openRequests: number;
  closedRequests: number;
  waitingForCustomer: number;
  inProgress: number;
  unreadByAdmin: number;
  unreadByUser: number;
  byUserType: {
    user: number;
    driver: number;
  };
  recentActivity: {
    id: number;
    description: string;
    timestamp: string;
  }[];
}

interface Stats {
  totalDrivers: number;
  verifiedDrivers: number;
  unverifiedDrivers: number;
  activeDrivers: number;
  totalUsers: number;
  dailyIncreases: {
    newDrivers: number;
    newVerifiedDrivers: number;
    newUnverifiedDrivers: number;
    newUsers: number;
  };
}

// Tab Panel Component
// Update the TabPanel component to allow scrolling
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
      style={{ 
        padding: '20px 0',
        height: 'calc(100vh - 120px)', // Set a fixed height minus the tabs and margin
        overflowY: 'auto' // Enable vertical scrolling
      }}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
};

// Default map center (adjust as needed)
const defaultPosition = [8.5241, 76.9366];

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [driverData, setDriverData] = useState<DriverData[]>([]);
  const [driverError, setDriverError] = useState<string | null>(null);
  const [driverLoading, setDriverLoading] = useState<boolean>(true);
  
  // Driver & User Stats
  const [stats, setStats] = useState<Stats | null>(null);
  const [statsLoading, setStatsLoading] = useState<boolean>(true);
  
  // Support stats state
  const [supportStats, setSupportStats] = useState<SupportStats | null>(null);
  const [supportStatsLoading, setSupportStatsLoading] = useState<boolean>(true);
  const [supportStatsError, setSupportStatsError] = useState<string | null>(null);
  const [recentTickets, setRecentTickets] = useState<SupportRequest[]>([]);

  const [rideStats, setRideStats] = useState<{
    completed: number;
    driverCancelled: number;
    userCancelled: number;
    aborted: number;
    noDriversAvailable: number;
  }>({
    completed: 0,
    driverCancelled: 0,
    userCancelled: 0,
    aborted: 0,
    noDriversAvailable: 0
  });
  const [selectedRideDate, setSelectedRideDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  });
  const [showAllTime, setShowAllTime] = useState<boolean>(false);
  const [rideStatsLoading, setRideStatsLoading] = useState<boolean>(true);

  // Define fetchDriverData at the component level
  const fetchDriverData = async () => {
    setDriverLoading(true);
    try {
      const response = await getAllActiveDrivers();
      if (response.data) {
        console.log(response.data);
        setDriverData(response.data);
      }
    } catch (err: any) {
      if (err.response && err.response.status === 401) {
        console.error("Unauthorized access. Redirecting to login...");
        navigate("/login");
      } else {
        setDriverError("Failed to fetch drivers");
        console.log(err);
      }
    } finally {
      setDriverLoading(false);
    }
  };

  // Use fetchDriverData in useEffect
  useEffect(() => {
    fetchDriverData();
    
    // Only fetch stats initially if we're on the Analytics tab
    if (tabValue === 2) {
      fetchStats();
    }
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // Fetch support data when switching to support tab
    if (newValue === 1 && !supportStats) {
      fetchSupportStats();
    }

    // Fetch driver and user stats when switching to analytics tab
    if (newValue === 2 && (!stats)) {
      fetchStats();
    }
  };

  // Fetch driver and user stats
  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      // Fetch driver stats
      const statsResponse = await getStats();
      if (statsResponse?.data) {
        setStats(statsResponse.data);
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    } finally {
      setStatsLoading(false);
    }
  };

  // Fetch support stats
  const fetchSupportStats = async () => {
    setSupportStatsLoading(true);
    try {
      const response = await getSupportRequests(1, 100);
      
      if (response?.data?.data) {
        const tickets = response.data.data;
        setRecentTickets(tickets.slice(0, 5)); // Store 5 most recent tickets

        // Calculate overall stats
        const openRequests = tickets.filter(t => t.status === 'open').length;
        const closedRequests = tickets.filter(t => t.status === 'closed').length;
        const waitingForCustomer = tickets.filter(t => t.status === 'waiting_for_customer').length;
        const inProgress = tickets.filter(t => t.status === 'in_progress').length;
        
        // Calculate unread messages
        const unreadByAdmin = tickets.reduce((count, ticket) => {
          return count + (ticket.conversations?.filter(
            c => c.senderType === 'user' && !c.isReadByAdmin
          ).length || 0);
        }, 0);

        const unreadByUser = tickets.reduce((count, ticket) => {
          return count + (ticket.conversations?.filter(
            c => c.senderType === 'admin' && !c.isReadByUser
          ).length || 0);
        }, 0);

        // Calculate stats by user type
        const userTickets = tickets.filter(t => t.userType === 'user').length;
        const driverTickets = tickets.filter(t => t.userType === 'driver').length;
        
        // Build recent activity
        const recentActivity = tickets.slice(0, 10).map(ticket => ({
          id: ticket.id,
          description: `Ticket #${ticket.id}: ${ticket.subject}`,
          timestamp: ticket.updated_at
        }));

        // Set the stats
        setSupportStats({
          totalRequests: tickets.length,
          openRequests,
          closedRequests,
          waitingForCustomer,
          inProgress,
          unreadByAdmin,
          unreadByUser,
          byUserType: {
            user: userTickets,
            driver: driverTickets
          },
          recentActivity
        });
      }
    } catch (error: any) {
      console.error('Error fetching support stats:', error);
      setSupportStatsError('Failed to load support dashboard data');
    } finally {
      setSupportStatsLoading(false);
    }
  };

  // Fetch ride statistics
  const fetchRideStats = async () => {
    setRideStatsLoading(true);
    try {
      const response = await getAllRides(1, 10000);
      if (response.data) {
        const rides = response.data.data;
        
        // Filter by date if selected and not showing all time
        const filteredRides = (!showAllTime && selectedRideDate)
          ? rides.filter(ride => {
              const rideDate = parseISO(ride.created_at);
              const filterDate = new Date(selectedRideDate + 'T00:00:00.000Z');
              return isSameDay(rideDate, filterDate);
            })
          : rides;

        // Calculate statistics
        const stats = {
          completed: filteredRides.filter(ride => ride.status === TripStatus.Completed).length,
          driverCancelled: filteredRides.filter(ride => ride.status === TripStatus.DriverCancelled).length,
          userCancelled: filteredRides.filter(ride => ride.status === TripStatus.UserCancelled).length,
          aborted: filteredRides.filter(ride => ride.status === TripStatus.Aborted).length,
          noDriversAvailable: filteredRides.filter(ride => ride.status === TripStatus.NoDriversAvailable).length
        };

        setRideStats(stats);
      }
    } catch (err) {
      console.error("Failed to fetch ride statistics:", err);
    } finally {
      setRideStatsLoading(false);
    }
  };

  // Effect for fetching ride stats
  useEffect(() => {
    fetchRideStats();
  }, [selectedRideDate, showAllTime]);

  // Stats Card Component
  const StatCard = ({ title, value, icon, trend = 0, color = 'primary' }) => {
    const trendIcon = trend >= 0 ? <TrendingUpIcon color="success" /> : <TrendingDownIcon color="error" />;
    const trendText = Math.abs(trend);
    
    return (
      <Grid item xs={12} sm={6} md={4} lg={2.4}>
        <Card sx={{ 
          height: '100%',
          boxShadow: 3,
          transition: 'transform 0.3s',
          '&:hover': { transform: 'translateY(-5px)', boxShadow: 6 }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ color: `${color}.main` }}>
                {icon}
              </Box>
              {trend !== null && (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {trendIcon}
                  <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {trendText}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {title}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  // Add refresh functions
  const handleRefreshStats = () => {
    setStatsLoading(true);
    fetchStats();
  };

  const handleRefreshRideStats = () => {
    setRideStatsLoading(true);
    fetchRideStats();
  };

  // Update the refresh handler to use the defined function
  const handleRefreshDriverData = () => {
    setDriverLoading(true);
    fetchDriverData();
  };

  // Live Vehicle Map Tab
  const renderVehicleMap = () => {
    if (driverLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (driverError) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography variant="h6" color="error">{driverError}</Typography>
        </Box>
      );
    }

    return (
      <div className="container">        
        <div className="mapContainer">
          <MapContainer
            center={defaultPosition}
            zoom={13}
            style={{ height: "60vh", width: "100%" }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; <a href='https://www.openstreetmap.org/copyright'>OpenStreetMap</a> contributors"
            />
            {driverData.map((vehicle) => (
              <Marker
                key={vehicle.id}
                position={[vehicle.current_location.latitude, vehicle.current_location.longitude]}
                icon={icon}
              >
                <Popup>
                  <strong>{vehicle.name}</strong>
                  <br />
                  Status: {vehicle.status}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>
      </div>
    );
  };

  // Support Dashboard Tab
  const renderSupportDashboard = () => {
    if (supportStatsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (supportStatsError) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography variant="h6" color="error">{supportStatsError}</Typography>
        </Box>
      );
    }

    if (!supportStats) {
      return (
        <Box sx={{ p: 4 }}>
          <Typography variant="h6">No data available</Typography>
        </Box>
      );
    }

    // Prepare data for status chart
    const statusData = [
      { name: 'Open', value: supportStats.openRequests, color: '#1976d2' },
      { name: 'In Progress', value: supportStats.inProgress, color: '#2196f3' },
      { name: 'Waiting for Customer', value: supportStats.waitingForCustomer, color: '#ff9800' },
      { name: 'Closed', value: supportStats.closedRequests, color: '#9e9e9e' }
    ];

    // Prepare data for user type chart
    const userTypeData = [
      { name: 'Customers', value: supportStats.byUserType.user, color: '#4caf50' },
      { name: 'Drivers', value: supportStats.byUserType.driver, color: '#f44336' }
    ];

    return (
      <Box sx={{ p: 3 }}>

        <Grid container spacing={3}>
          {/* Quick Stats */}
          <Grid item xs={12} md={6} lg={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">Total Requests</Typography>
              <Typography variant="h3">{supportStats.totalRequests}</Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">Open Requests</Typography>
              <Typography variant="h3" color="primary">
                {supportStats.openRequests + supportStats.inProgress + supportStats.waitingForCustomer}
              </Typography>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">Unread Messages</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Typography variant="h4" color="error" sx={{ mr: 2 }}>
                  {supportStats.unreadByAdmin}
                </Typography>
                <Typography variant="body2">
                  new messages to read
                </Typography>
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6} lg={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">Waiting for Customers</Typography>
              <Typography variant="h3" color="warning.main">
                {supportStats.waitingForCustomer}
              </Typography>
            </Paper>
          </Grid>

          {/* Recent Tickets */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400, overflowY: 'auto' }}>
              <Typography variant="h6" gutterBottom>Recent Tickets</Typography>
              {recentTickets.length > 0 ? (
                recentTickets.map((ticket) => (
                  <Card key={ticket.id} sx={{ mb: 2 }}>
                    <CardContent>
                      <Typography variant="h6">#{ticket.id}: {ticket.subject}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        From: {ticket.userType === 'user' ? 'Customer' : 'Driver'} #{ticket.userId || ticket.driverId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Status: {ticket.status}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Created: {new Date(ticket.created_at).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent tickets
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Recent Activity */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400, overflowY: 'auto' }}>
              <Typography variant="h6" gutterBottom>Recent Activity</Typography>
              {supportStats.recentActivity.length > 0 ? (
                supportStats.recentActivity.map((activity, index) => (
                  <Box key={index} sx={{ mb: 2, pb: 1, borderBottom: '1px solid #eee' }}>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(activity.timestamp).toLocaleString()}
                    </Typography>
                    <Typography variant="body1">{activity.description}</Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No recent activity
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    );
  };

  // Analytics Dashboard Tab
  const renderAnalyticsDashboard = () => {
    if (statsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // Group drivers by location
    const locationStats = driverData.reduce((acc, driver) => {
      const location = driver.current_location?.address || 'Unknown Location';
      if (!acc[location]) {
        acc[location] = {
          count: 0,
          drivers: []
        };
      }
      acc[location].count++;
      acc[location].drivers.push(driver);
      return acc;
    }, {} as Record<string, { count: number; drivers: DriverData[] }>);

    // Convert to array and sort by count
    const locationStatsArray = Object.entries(locationStats)
      .map(([location, stats]) => ({
        location,
        count: stats.count,
        drivers: stats.drivers
      }))
      .sort((a, b) => b.count - a.count);

    return (
      <Box sx={{ p: 3 }}>
        
        {/* Stats Cards */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Typography variant="h5" sx={{ color: 'primary.main' }}>Driver & User Statistics</Typography>
            <IconButton 
              onClick={handleRefreshStats} 
              color="primary"
              size="small"
              sx={{ 
                '&:hover': { transform: 'rotate(180deg)', transition: 'transform 0.3s' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
          <Grid container spacing={3}>
            <StatCard 
              title="Total Drivers" 
              value={stats?.totalDrivers || 0} 
              trend={stats?.dailyIncreases.newDrivers} 
              icon={<PersonIcon fontSize="large" />}
              color="primary"
            />
            <StatCard 
              title="Verified Drivers" 
              value={stats?.verifiedDrivers || 0} 
              trend={stats?.dailyIncreases.newVerifiedDrivers} 
              icon={<VerifiedIcon fontSize="large" />}
              color="success"
            />
            <StatCard 
              title="Active Drivers" 
              value={stats?.activeDrivers || 0}
              icon={<DriveEtaIcon fontSize="large" />}
              color="info"
            />
            <StatCard 
              title="Unverified Drivers" 
              value={stats?.unverifiedDrivers || 0} 
              trend={stats?.dailyIncreases.newUnverifiedDrivers} 
              icon={<PendingIcon fontSize="large" />}
              color="warning"
            />
            <StatCard 
              title="Total Users" 
              value={stats?.totalUsers || 0} 
              trend={stats?.dailyIncreases.newUsers} 
              icon={<GroupIcon fontSize="large" />}
              color="secondary"
            />
          </Grid>
        </Box>

        {/* Ride Statistics Section */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            mb: 2
          }}>
            <Typography variant="h5" sx={{ color: 'primary.main' }}>
              <DirectionsCarIcon sx={{ mr: 1 }} /> Ride Statistics
            </Typography>
            <IconButton 
              onClick={handleRefreshRideStats} 
              color="primary"
              size="small"
              sx={{ 
                '&:hover': { transform: 'rotate(180deg)', transition: 'transform 0.3s' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>

          {/* Date Filter */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
            <input
              type="date"
              value={selectedRideDate}
              onChange={(e) => {
                setSelectedRideDate(e.target.value);
                setShowAllTime(false);
              }}
              disabled={showAllTime}
              style={{
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                fontSize: '14px',
                opacity: showAllTime ? 0.5 : 1
              }}
            />
            <Button 
              variant="outlined"
              onClick={() => {
                const today = new Date();
                setSelectedRideDate(today.toISOString().split('T')[0]);
                setShowAllTime(false);
              }}
              size="small"
            >
              Reset to Today
            </Button>
            <Button 
              variant="contained"
              onClick={() => {
                setShowAllTime(!showAllTime);
              }}
              size="small"
              color={showAllTime ? "secondary" : "primary"}
            >
              {showAllTime ? "Show Today" : "Show All Time"}
            </Button>
          </Box>

          {rideStatsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                  {showAllTime ? "Showing statistics for all time" : `Showing statistics for ${selectedRideDate}`}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%',
                  bgcolor: 'success.light',
                  color: 'success.contrastText'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CheckCircleIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Completed Rides</Typography>
                    </Box>
                    <Typography variant="h3">{rideStats.completed}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%',
                  bgcolor: 'error.light',
                  color: 'error.contrastText'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CancelIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Driver Cancelled</Typography>
                    </Box>
                    <Typography variant="h3">{rideStats.driverCancelled}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%',
                  bgcolor: 'warning.light',
                  color: 'warning.contrastText'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <CancelIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">User Cancelled</Typography>
                    </Box>
                    <Typography variant="h3">{rideStats.userCancelled}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%',
                  bgcolor: 'error.dark',
                  color: 'error.contrastText'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ErrorIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">Aborted Rides</Typography>
                    </Box>
                    <Typography variant="h3">{rideStats.aborted}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%',
                  bgcolor: 'info.light',
                  color: 'info.contrastText'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ErrorIcon sx={{ mr: 1 }} />
                      <Typography variant="h6">No Drivers Available</Typography>
                    </Box>
                    <Typography variant="h3">{rideStats.noDriversAvailable}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Location Distribution Section */}
        <Box sx={{ mt: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 1,
            mb: 2
          }}>
            <Typography variant="h5" sx={{ color: 'primary.main' }}>
              <GroupIcon sx={{ mr: 1 }} /> Location-wise Driver Distribution
            </Typography>
            <IconButton 
              onClick={handleRefreshDriverData} 
              color="primary"
              size="small"
              sx={{ 
                '&:hover': { transform: 'rotate(180deg)', transition: 'transform 0.3s' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Box>
          
          <Grid container spacing={3}>
            {/* Summary Cards */}
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                height: '100%',
                bgcolor: 'primary.light',
                color: 'primary.contrastText'
              }}>
                <CardContent>
                  <Typography variant="h6">Total Locations</Typography>
                  <Typography variant="h3">{locationStatsArray.length}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ 
                height: '100%',
                bgcolor: 'success.light',
                color: 'success.contrastText'
              }}>
                <CardContent>
                  <Typography variant="h6">Most Populated Area</Typography>
                  <Typography variant="h6" noWrap>
                    {locationStatsArray[0]?.location || 'N/A'}
                  </Typography>
                  <Typography variant="h4">
                    {locationStatsArray[0]?.count || 0} drivers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Simplified Table */}
            <Grid item xs={12}>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  mt: 2,
                  boxShadow: 3,
                  borderRadius: 2,
                  overflow: 'hidden',
                  height: 'calc(100vh - 400px)', // Dynamic height based on viewport
                  minHeight: '400px' // Minimum height to ensure usability
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'primary.main' }}>
                      <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Location</TableCell>
                      <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Number of Drivers</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {locationStatsArray.map((stat) => (
                      <TableRow 
                        key={stat.location}
                        sx={{ 
                          '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                          '&:hover': { bgcolor: 'action.selected' }
                        }}
                      >
                        <TableCell component="th" scope="row">
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {stat.location}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6" color="primary">
                            {stat.count}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', // Full viewport height
      display: 'flex',
      flexDirection: 'column'
    }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="dashboard tabs"
          variant="fullWidth"
        >
          <Tab label="Live Vehicle Map" id="dashboard-tab-0" />
          <Tab label="Support Dashboard" id="dashboard-tab-1" />
          <Tab label="Analytics" id="dashboard-tab-2" />
        </Tabs>
      </Box>
      
      <Box sx={{ flex: 1, overflow: 'hidden' }}> {/* This container will take remaining height */}
        <TabPanel value={tabValue} index={0}>
          {renderVehicleMap()}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          {renderSupportDashboard()}
        </TabPanel>
        
        <TabPanel value={tabValue} index={2}>
          {renderAnalyticsDashboard()}
        </TabPanel>
      </Box>
    </Box>
  );
};

export default Dashboard;